import React from 'react';
import { Box, Stack, Typography, Paper, Button as MuiButton } from '@mui/material';
import {
  AddCircleIcons,
  SubtractCircleIcons,
} from '../../../../../icons/icons';
import { Button, Drawer, TableColumn } from '../../../../../ui';
import { Column } from '../Functions/GetColumns';

export type ColumnConfigurationProps<T> = {
  isOpen: boolean;
  onCloseDrawer: () => void;
  columns: Column[];
  setColumns: (value: ((prevState: Column[]) => Column[]) | Column[]) => void;
  getColumns: TableColumn<T>[];
  jobSkills: string[];
};

export const ColumnConfiguration = <T extends object>({
  isOpen,
  onCloseDrawer,
  columns,
  setColumns,
  getColumns,
  jobSkills,
}: ColumnConfigurationProps<T>) => {
  const allColumns = jobSkills.map((skill) => {
    const upperCased = skill.toUpperCase();
    return {
      key: upperCased,
      label: upperCased,
      width: '250px',
      align: 'left' as const,
      disableSort: true,
    };
  });

  const getColumnKeys = new Set(getColumns.map((item) => item.key));
  const difference = allColumns.filter((item) => !getColumnKeys.has(item.key));

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onCloseDrawer}
      footerContent={
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2.5, width: '100%' }}>
          <Button label='Close' onClick={onCloseDrawer} variant='secondary' />
        </Box>
      }
      title='Select Column'
      width='w-full md:w-3/5'
    >
      <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '50%',
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Column List
          </Typography>
          <Stack spacing={1}>
            {difference.map((column) => (
              <MuiButton
                key={column.key}
                variant='outlined'
                size='small'
                startIcon={<AddCircleIcons />}
                onClick={() => {
                  setColumns([...columns, { title: column.label }]);
                }}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: '9999px',
                  textTransform: 'none',
                  bgcolor: 'background.paper',
                }}
              >
                {column.label}
              </MuiButton>
            ))}
          </Stack>
        </Paper>

        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            width: '50%',
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 2,
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Selected Columns
          </Typography>
          <Stack spacing={1}>
            {getColumns.map((column) => (
              <MuiButton
                key={String(column.key)}
                variant='outlined'
                size='small'
                startIcon={<SubtractCircleIcons />}
                onClick={() => {
                  setColumns(columns.filter((c) => c.title !== column.label));
                }}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: '9999px',
                  textTransform: 'none',
                  bgcolor: 'background.paper',
                }}
              >
                {column.label}
              </MuiButton>
            ))}
          </Stack>
        </Paper>
      </Box>
    </Drawer>
  );
};

export default ColumnConfiguration;
